import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import logo from '../../../../images/logo.png';
import { getAppointmentsByDocument, cancelAppointmentByDocument, getCompanyInfo } from '../../../../services/PublicBookingService';
import { formatNumberWithTwoDecimals } from '../../../../utils/FormatUtils';
import ClientTabBar from '../shared/ClientTabBar';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import ImageWithSkeleton from '../../../components/Custom/Common/ImageWithSkeleton';
import { getStoredCustomerDocument } from '../../../../utils/PublicBookingStorage';
import {
    isCompleteIdentifier,
    isEmailIdentifier,
    toIdentifierParam
} from '../../../../utils/PublicBookingIdentifier';
import './PublicBooking.css';

const HISTORY_PAGE_SIZE = 6;
const UPCOMING_STATUSES = new Set(['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS']);
const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const getHeroStyle = (companyInfo) => {
    if (!companyInfo?.bannerUrl) {
        return companyInfo?.headerColor ? { background: companyInfo.headerColor } : undefined;
    }

    return {
        backgroundColor: companyInfo?.headerColor || '#232c3a',
    };
};

const PublicMyAppointmentsPage = () => {
    const { t } = useTranslation();
    const { companyUuid } = useParams();

    const [cpf] = useState(() => getStoredCustomerDocument(companyUuid));
    const hasSessionCustomer = isCompleteIdentifier(cpf);
    const [loading, setLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [activeList, setActiveList] = useState('upcoming');
    const [visibleHistoryCount, setVisibleHistoryCount] = useState(HISTORY_PAGE_SIZE);

    // Modal de cancelamento
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellingAppointment, setCancellingAppointment] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);
    useEffect(() => {
        getCompanyInfo(companyUuid)
            .then((response) => setCompanyInfo(response.data))
            // A consulta de agendamentos continua disponível com a identidade neutra
            // quando somente a configuração visual da empresa não puder ser carregada.
            .catch(() => setCompanyInfo(null));
    }, [companyUuid]);

    const brandColor = companyInfo?.headerColor || '#333';
    const brandVars = {
        '--booking-accent': brandColor,
        '--booking-accent-strong': `color-mix(in srgb, ${brandColor} 80%, #000)`,
        '--booking-accent-soft': `color-mix(in srgb, ${brandColor} 12%, #fff)`,
        '--booking-ink': '#1b2333',
        '--booking-muted': '#728095',
        '--booking-line': '#dbe3ec',
        '--booking-surface-alt': '#f4f7fb'
    };

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!isCompleteIdentifier(cpf)) {
            setError(t(isEmailIdentifier(cpf) ? 'validation.invalid.email' : 'validation.invalid.cpf'));
            return;
        }

        try {
            setLoading(true);
            setError('');
            const response = await getAppointmentsByDocument(companyUuid, toIdentifierParam(cpf));
            setAppointments(response.data);
            setActiveList('upcoming');
            setVisibleHistoryCount(HISTORY_PAGE_SIZE);
            setSearched(true);
        } catch (err) {
            if (err.response?.status === 404) {
                setAppointments([]);
                setSearched(true);
            } else {
                setError(t('booking.search_error'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!hasSessionCustomer || !isCompleteIdentifier(cpf) || searched) return;
        handleSearch({ preventDefault: () => {} });
        // A função usa o estado local da página e só é executada uma vez ao abrir a rota
        // através do fluxo autenticado do agendamento.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSessionCustomer, companyUuid]);

    const openCancelModal = (appointment) => {
        setCancellingAppointment(appointment);
        setCancelReason('');
        setShowCancelModal(true);
    };

    const handleCancel = async () => {
        if (!cancellingAppointment) return;

        try {
            setCancelling(true);
            await cancelAppointmentByDocument(
                companyUuid,
                cancellingAppointment.uuid,
                toIdentifierParam(cpf),
                cancelReason
            );

            // O item sai de "Próximos" e passa a compor o histórico sem exigir nova busca.
            setAppointments(prev => prev.map(appointment => appointment.uuid === cancellingAppointment.uuid
                ? { ...appointment, status: 'CANCELED' }
                : appointment));
            setShowCancelModal(false);
            setCancellingAppointment(null);
        } catch (err) {
            setError(err.response?.data?.message || t('booking.cancel_error'));
        } finally {
            setCancelling(false);
        }
    };

    // A API devolve scheduledDate como data e hora ISO (LocalDateTime), num campo só.
    const formatDate = (scheduledDate) => {
        if (!scheduledDate) return '';
        return new Date(scheduledDate).toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
    };

    const formatTime = (scheduledDate) => {
        if (!scheduledDate) return '';
        return new Date(scheduledDate).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Cadastros antigos duplicados para o mesmo CPF continuam aparecendo sem perder histórico.
    const hasMultipleCustomers = new Set(
        appointments.map((appointment) => appointment.customer?.id).filter(Boolean)
    ).size > 1;

    const getStatusBadge = (status) => {
        const statusMap = {
            'SCHEDULED': { bg: 'primary', label: t('booking.status.scheduled') },
            'CONFIRMED': { bg: 'success', label: t('booking.status.confirmed') },
            'IN_PROGRESS': { bg: 'warning', label: t('booking.status.in_progress') },
            'COMPLETED': { bg: 'secondary', label: t('booking.status.completed') },
            'CANCELLED': { bg: 'danger', label: t('booking.status.cancelled') },
            'CANCELED': { bg: 'danger', label: t('booking.status.cancelled') },
            'NO_SHOW': { bg: 'dark', label: t('booking.status.no_show') }
        };
        const config = statusMap[status] || { bg: 'secondary', label: status };
        return <Badge bg={config.bg}>{config.label}</Badge>;
    };

    const canCancel = (status) => {
        return ['SCHEDULED', 'CONFIRMED'].includes(status);
    };

    const upcomingAppointments = appointments
        .filter((appointment) => UPCOMING_STATUSES.has(appointment.status))
        .sort((first, second) => new Date(first.scheduledDate) - new Date(second.scheduledDate));
    const historyAppointments = appointments
        .filter((appointment) => !UPCOMING_STATUSES.has(appointment.status))
        .sort((first, second) => new Date(second.scheduledDate) - new Date(first.scheduledDate));
    const visibleAppointments = activeList === 'upcoming'
        ? upcomingAppointments
        : historyAppointments.slice(0, visibleHistoryCount);

    return (
        <div className="public-booking-page public-my-appointments-page" style={brandVars}>
            <div className="booking-hero" style={getHeroStyle(companyInfo)}>
                {companyInfo?.bannerUrl && (
                    <div
                        className="booking-hero-banner-layer"
                        aria-hidden="true"
                        style={{
                            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url(${resolvePublicAssetUrl(companyInfo.bannerUrl)})`,
                            backgroundPosition: `${companyInfo?.bannerPositionX ?? 50}% ${companyInfo?.bannerPositionY ?? 50}%`,
                            backgroundSize: 'cover',
                            transform: `scale(${(companyInfo?.bannerZoom ?? 100) / 100})`,
                            transformOrigin: `${companyInfo?.bannerPositionX ?? 50}% ${companyInfo?.bannerPositionY ?? 50}%`,
                        }}
                    />
                )}
                <div className="booking-hero-content booking-hero-content--compact">
                    <div className="booking-hero-brand">
                        {companyInfo?.showLogoOnBookingPage !== false && (
                            <div className="booking-hero-logo">
                                {companyInfo?.logoUrl ? (
                                    <ImageWithSkeleton src={resolvePublicAssetUrl(companyInfo.logoUrl)} alt={companyInfo.name} fill onDark />
                                ) : <img src={logo} alt="Colmeia Barber" />}
                            </div>
                        )}
                        {companyInfo?.showCompanyNameOnBookingPage !== false && <span className="booking-hero-brand__name">{companyInfo?.name}</span>}
                    </div>
                    <div className="booking-hero-copy">
                        <h1 className="booking-hero-name">{t('booking.my_appointments')}</h1>
                        <p className="booking-hero-sub">{t('booking.my_appointments_description')}</p>
                    </div>
                </div>
            </div>

            <Container className="booking-container my-appointments-container pb-4">
                <div className="booking-workspace">
                <div className="booking-toolbar">
                    <div>
                        <span className="booking-toolbar-eyebrow">{t('booking.online_booking')}</span>
                        <h2 className="booking-online-title">{t('booking.my_appointments')}</h2>
                        <p className="text-muted mb-0">{t('booking.my_appointments_description')}</p>
                    </div>
                    <Link
                        to={`/booking/${companyUuid}`}
                        state={{ startBookingFresh: true }}
                        className="booking-toolbar__back"
                    >
                        <i className="fa fa-arrow-left me-2"></i>
                        {t('booking.back_to_booking')}
                    </Link>
                </div>

                {/* A identificação acontece no agendamento; esta aba usa o cliente já lembrado. */}
                {!hasSessionCustomer && (
                    <Card className="booking-card shadow-lg mb-4">
                        <Card.Body className="p-4 text-center">
                            <p>Identifique-se no agendamento para consultar seus horários.</p>
                            <Link to={`/booking/${companyUuid}`} className="btn btn-primary">Ir para agendamento</Link>
                        </Card.Body>
                    </Card>
                )}
                {hasSessionCustomer && loading && <div className="text-center p-4"><Spinner animation="border" /><span className="visually-hidden">Carregando agendamentos</span></div>}
                {hasSessionCustomer && error && (
                    <Alert variant="danger" role="alert">
                        {error} <Button variant="link" onClick={handleSearch}>Tentar novamente</Button>
                    </Alert>
                )}

                {/* Lista de agendamentos */}
                {searched && (
                    <div className="appointments-list">
                        {appointments.length === 0 ? (
                            <Card className="booking-card shadow-lg text-center">
                                <Card.Body className="py-5">
                                    <i className="fa fa-calendar-times fa-3x text-muted mb-3"></i>
                                    <h5 className="text-muted">{t('booking.no_appointments_found')}</h5>
                                    <p className="text-muted small">{t('booking.no_appointments_hint')}</p>
                                </Card.Body>
                            </Card>
                        ) : (
                            <>
                            <div className="appointments-tabs" role="tablist" aria-label={t('booking.appointment_views')}>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeList === 'upcoming'}
                                    className={activeList === 'upcoming' ? 'is-active' : ''}
                                    onClick={() => setActiveList('upcoming')}
                                >
                                    {t('booking.upcoming_appointments')}
                                    <span>{upcomingAppointments.length}</span>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeList === 'history'}
                                    className={activeList === 'history' ? 'is-active' : ''}
                                    onClick={() => setActiveList('history')}
                                >
                                    {t('booking.appointment_history')}
                                    <span>{historyAppointments.length}</span>
                                </button>
                            </div>

                            {visibleAppointments.length === 0 ? (
                                <Card className="booking-card text-center appointments-empty-state">
                                    <Card.Body className="py-4">
                                        <i className="fa fa-calendar-check fa-2x text-muted mb-2"></i>
                                        <p className="text-muted mb-0">
                                            {activeList === 'upcoming'
                                                ? t('booking.no_upcoming_appointments')
                                                : t('booking.no_appointment_history')}
                                        </p>
                                    </Card.Body>
                                </Card>
                            ) : visibleAppointments.map((appointment) => (
                                <Card key={appointment.uuid} className="booking-card shadow-lg mb-3 appointment-card">
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                {hasMultipleCustomers && appointment.customer?.fullName && (
                                                    <span className="appointment-customer-name d-block mb-1">
                                                        <i className="fa fa-user me-1"></i>
                                                        {appointment.customer.fullName}
                                                    </span>
                                                )}
                                                <h5 className="mb-1 text-capitalize">
                                                    {formatDate(appointment.scheduledDate)}
                                                </h5>
                                                <span className="text-primary fw-bold">
                                                    <i className="fa fa-clock me-1"></i>
                                                    {formatTime(appointment.scheduledDate)}
                                                </span>
                                            </div>
                                            <div className="appointment-card-status">
                                                {appointment.encaixe && (
                                                    <Badge bg="warning" text="dark">
                                                        {t('booking.reception_walk_in')}
                                                    </Badge>
                                                )}
                                                {getStatusBadge(appointment.status)}
                                            </div>
                                        </div>

                                        <div className="appointment-details">
                                            <div className="detail-item mb-2">
                                                <i className="fa fa-user-tie me-2 text-muted"></i>
                                                <span>{appointment.employee?.fullName}</span>
                                            </div>

                                            <div className="detail-item mb-2">
                                                <i className="fa fa-cut me-2 text-muted"></i>
                                                <span>{appointment.service?.name}</span>
                                            </div>

                                            <div className="detail-item mb-2">
                                                <i className="fa fa-building me-2 text-muted"></i>
                                                <span>{appointment.branch?.name}</span>
                                            </div>

                                            {appointment.price != null && (
                                                <div className="detail-item">
                                                    <i className="fa fa-money-bill me-2 text-muted"></i>
                                                    <span className="fw-bold text-primary">
                                                        R$ {formatNumberWithTwoDecimals(appointment.price)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Código do agendamento */}
                                        <div className="appointment-code mt-3 pt-3 border-top">
                                            <small className="text-muted">
                                                {t('booking.code')}:
                                                <span className="font-monospace ms-1 fw-bold">
                                                    {appointment.uuid?.substring(0, 8).toUpperCase()}
                                                </span>
                                            </small>
                                        </div>

                                        {/* Botão de cancelar */}
                                        {canCancel(appointment.status) && (
                                            <div className="mt-3">
                                                <Button
                                                    variant="danger"
                                                    className="my-appointments-cancel-button"
                                                    size="sm"
                                                    onClick={() => openCancelModal(appointment)}
                                                >
                                                    <i className="fa fa-times me-1"></i>
                                                    {t('booking.cancel_appointment')}
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            ))}

                            {activeList === 'history' && visibleHistoryCount < historyAppointments.length && (
                                <Button
                                    variant="outline-secondary"
                                    className="appointments-load-more"
                                    onClick={() => setVisibleHistoryCount(count => count + HISTORY_PAGE_SIZE)}
                                >
                                    {t('booking.load_more_appointments')}
                                </Button>
                            )}
                            </>
                        )}
                    </div>
                )}
                </div>

                <PublicBrandSignature className="is-compact" />
            </Container>

            {/* Modal de cancelamento */}
            <Modal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                centered
                className="booking-cancel-modal"
                style={brandVars}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('booking.cancel_appointment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">{t('booking.cancel_confirmation')}</p>

                    {cancellingAppointment && (
                        <Card className="cancel-appointment-summary mb-3">
                            <Card.Body className="py-2">
                                <strong className="text-capitalize">
                                    {formatDate(cancellingAppointment.scheduledDate)}
                                </strong>
                                <span className="ms-2">{t('booking.at')} {formatTime(cancellingAppointment.scheduledDate)}</span>
                                {cancellingAppointment.customer?.fullName && (
                                    <span className="d-block text-muted small mt-1">
                                        {cancellingAppointment.customer.fullName}
                                    </span>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    <Form.Group>
                        <Form.Label>{t('booking.cancel_reason')} <span className="text-muted">({t('optional')})</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder={t('booking.cancel_reason_placeholder')}
                            rows={4}
                            maxLength={300}
                            className="cancel-reason-input"
                        />
                        <div className="cancel-reason-counter">
                            {cancelReason.length}/300
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        className="cancel-modal-close-button"
                        onClick={() => setShowCancelModal(false)}
                        disabled={cancelling}
                    >
                        {t('close')}
                    </Button>
                    <Button variant="danger" onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t('cancelling')}
                            </>
                        ) : (
                            <>
                                <i className="fa fa-times me-2"></i>
                                {t('booking.confirm_cancel')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ClientTabBar companyUuid={companyUuid} />
        </div>
    );
};

export default PublicMyAppointmentsPage;
